import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { rootUrl, formatDate, formatToZMW, handlePrint, handleExcel } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { toast } from 'react-toastify';
import {
  FaSpinner,
  FaDownload,
  FaPrint,
  FaChartLine,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoice,
  FaFilter,
} from 'react-icons/fa';
import Select from 'react-select';

const DETAIL_COLUMNS = [
  { key: 'sn', label: '#' },
  { key: 'receipt_no', label: 'Receipt No.' },
  { key: 'receipt_date', label: 'Receipt Date' },
  { key: 'revenue_amount', label: 'Revenue Collected' },
  { key: 'user_id', label: 'User ID' },
  { key: 'issued_by_name', label: 'Collected By' },
  { key: 'station', label: 'Station' },
  { key: 'transaction_code', label: 'Transaction Code' },
  { key: 'transaction_name', label: 'Transaction Description' },
  { key: 'fee_type', label: 'Fee Type' },
  { key: 'property_no', label: 'Property No. (Land)' },
  { key: 'property_location', label: 'Property Location' },
  { key: 'payee_name', label: 'Name of Payee' },
  { key: 'owner_id', label: 'Owner ID' },
  { key: 'owner_nrc', label: 'Owner NRC' },
  { key: 'invoice_no', label: 'Invoice No.' },
  { key: 'bank_name', label: 'Bank' },
  { key: 'source_of_fund', label: 'Source of Fund' },
  { key: 'program_code', label: 'Program' },
  { key: 'subprogram_name', label: 'Subprogram' },
  { key: 'payment_reference', label: 'Payment Ref.' },
  { key: 'receipt_narration', label: 'Narration' },
];

function cellValue(row, key, index) {
  switch (key) {
    case 'sn':
      return index + 1;
    case 'receipt_date':
      return row[key] ? formatDate(row[key]) : '-';
    case 'revenue_amount':
      return formatToZMW(parseFloat(row[key]) || 0);
    default:
      return row[key] ?? '-';
  }
}

export default function BillReport({ startDate, endDate, triggerFetch }) {
  const auth = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('register');
  const [stationFilter, setStationFilter] = useState(null);
  const [txnFilter, setTxnFilter] = useState(null);
  const [search, setSearch] = useState('');
  const printRef = useRef();

  const fetchData = async () => {
    try {
      setLoading(true);
      const payload = `${auth.user.cid}~${startDate}~${endDate}`;
      const res = await axios.get(rootUrl(`/revenueCollectionReport/${encodeURIComponent(payload)}`));
      if (res.data?.status !== 200) {
        throw new Error(res.data?.message || 'Could not load revenue collection report');
      }
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setSummary(res.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load report');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      fetchData();
    }
  }, [triggerFetch, startDate, endDate]);

  const stationOptions = useMemo(() => {
    const stations = [...new Set(rows.map((r) => r.station || 'Unassigned station'))];
    return stations.sort().map((s) => ({ value: s, label: s }));
  }, [rows]);

  const txnOptions = useMemo(() => {
    const codes = [...new Set(rows.map((r) => r.transaction_code).filter(Boolean))];
    return codes.sort().map((c) => ({ value: c, label: c }));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const stationOk = !stationFilter?.value || row.station === stationFilter.value;
      const txnOk = !txnFilter?.value || row.transaction_code === txnFilter.value;
      if (!term) return stationOk && txnOk;
      const haystack = [
        row.receipt_no,
        row.payee_name,
        row.property_no,
        row.fee_type,
        row.transaction_code,
        row.transaction_name,
        row.invoice_no,
        row.owner_nrc,
        row.issued_by_name,
        row.receipt_narration,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return stationOk && txnOk && haystack.includes(term);
    });
  }, [rows, stationFilter, txnFilter, search]);

  const filteredTotal = useMemo(
    () => filtered.reduce((sum, row) => sum + (parseFloat(row.revenue_amount) || 0), 0),
    [filtered]
  );

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FaChartLine },
    { id: 'register', label: 'Receipt Register', icon: FaFileInvoice },
    { id: 'station', label: 'By Station', icon: FaMapMarkerAlt },
    { id: 'fee', label: 'By Fee Type', icon: FaMoneyBillWave },
  ];

  return (
    <div ref={printRef} className="w-full space-y-6">
      {loading ? (
        <div className="flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm">
          <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
          <p className="text-gray-600 text-lg">Loading revenue collection report...</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <div className="bg-blue-950 rounded-lg p-2">
                  <FaMoneyBillWave className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="font-bold text-xl">Revenue Collection Database</h2>
                  <p className="text-gray-600 text-sm">
                    Receipt register for all revenues collected in the review period
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handlePrint(printRef)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-sm"
                >
                  <FaPrint /> <span className="hidden md:inline">Print</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleExcel(printRef, `Revenue_Collection_${startDate}_${endDate}`)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-sm"
                >
                  <FaDownload /> <span className="hidden md:inline">Excel</span>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <FaCalendarAlt />
              <span>
                Period: {formatDate(startDate)} – {formatDate(endDate)}
              </span>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <SummaryCards
              summary={summary}
              filteredTotal={filteredTotal}
              filteredCount={filtered.length}
            />

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3 print:hidden">
              <div className="flex items-center gap-2">
                <FaFilter className="text-blue-950" />
                <h3 className="font-semibold text-gray-800">Filters</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search receipt, payee, property, fee type..."
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                />
                <Select
                  options={stationOptions}
                  value={stationFilter}
                  onChange={setStationFilter}
                  placeholder="Filter by station"
                  isClearable
                  className="text-sm"
                />
                <Select
                  options={txnOptions}
                  value={txnFilter}
                  onChange={setTxnFilter}
                  placeholder="Filter by transaction code"
                  isClearable
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-b border-gray-200 print:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-t-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white border-b-2 border-blue-400'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon /> {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'summary' && <SummaryTab summary={summary} />}
            {activeTab === 'register' && (
              <ReceiptRegister rows={filtered} total={filteredTotal} />
            )}
            {activeTab === 'station' && (
              <BreakdownTable title="Collections by Station" rows={summary?.by_station || []} />
            )}
            {activeTab === 'fee' && (
              <BreakdownTable
                title="Collections by Fee Type"
                rows={(summary?.by_fee_type || []).map((r) => ({
                  label: r.fee_type,
                  count: r.count,
                  amount: r.amount,
                }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCards({ summary, filteredTotal, filteredCount }) {
  const cards = [
    {
      title: 'Total Revenue (Period)',
      value: formatToZMW(summary?.total_revenue || 0),
      subtitle: `${summary?.line_count || 0} receipt line(s)`,
      color: 'bg-green-600',
      icon: FaMoneyBillWave,
    },
    {
      title: 'Distinct Receipts',
      value: summary?.distinct_receipt_count ?? 0,
      subtitle: 'Unique receipt numbers',
      color: 'bg-blue-500',
      icon: FaFileInvoice,
    },
    {
      title: 'Properties with Collections',
      value: summary?.distinct_property_count ?? 0,
      subtitle: 'Linked land/property records',
      color: 'bg-purple-500',
      icon: FaMapMarkerAlt,
    },
    {
      title: 'Filtered View Total',
      value: formatToZMW(filteredTotal),
      subtitle: `${filteredCount} line(s) after filters`,
      color: 'bg-orange-500',
      icon: FaChartLine,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div
          key={card.title}
          className="bg-white rounded-lg shadow-md p-5 border-l-4 border-blue-900"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{card.title}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
              <p className="text-xs text-gray-500 mt-2">{card.subtitle}</p>
            </div>
            <div className={`p-3 rounded-full ${card.color} text-white`}>
              <card.icon className="h-5 w-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryTab({ summary }) {
  if (!summary) {
    return <p className="text-sm text-gray-600">No summary data for this period.</p>;
  }

  return (
    <div className="space-y-6">
      <BreakdownTable title="Collections by station" rows={summary.by_station || []} />
      <BreakdownTable
        title="Collections by transaction code"
        rows={(summary.by_transaction || []).map((r) => ({
          label: `${r.transaction_code} – ${r.transaction_name}`,
          count: r.count,
          amount: r.amount,
        }))}
      />
    </div>
  );
}

function BreakdownTable({ title, rows }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-600">No breakdown data available.</p>;
  }

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <h3 className="px-4 py-3 bg-gray-50 font-semibold text-gray-800 border-b">{title}</h3>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
            <th className="py-2 px-4 text-left">#</th>
            <th className="py-2 px-4 text-left">Description</th>
            <th className="py-2 px-4 text-right">Lines</th>
            <th className="py-2 px-4 text-right">Amount</th>
            <th className="py-2 px-4 text-right">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const share =
              total > 0 ? (((parseFloat(row.amount) || 0) / total) * 100).toFixed(1) : '0.0';
            return (
              <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50">
                <td className="py-2 px-4">{idx + 1}</td>
                <td className="py-2 px-4">{row.label || row.station || row.fee_type || '-'}</td>
                <td className="py-2 px-4 text-right">{row.count}</td>
                <td className="py-2 px-4 text-right font-medium">
                  {formatToZMW(parseFloat(row.amount) || 0)}
                </td>
                <td className="py-2 px-4 text-right text-gray-600">{share}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReceiptRegister({ rows, total }) {
  if (!rows.length) {
    return (
      <p className="text-sm text-gray-600 py-8 text-center">
        No revenue receipts found for this period and filter selection.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-xs md:text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
            {DETAIL_COLUMNS.map((col) => (
              <th key={col.key} className="py-2 px-2 text-left whitespace-nowrap font-semibold">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.receipt_line_id || rowIndex}
              className="border-b border-gray-100 hover:bg-blue-50"
            >
              {DETAIL_COLUMNS.map((col) => (
                <td
                  key={col.key}
                  className="py-2 px-2 text-gray-700 whitespace-nowrap max-w-[220px] truncate"
                  title={String(cellValue(row, col.key, rowIndex))}
                >
                  {cellValue(row, col.key, rowIndex)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-800 text-white font-bold">
            <td colSpan={3} className="py-3 px-2 text-right">
              Total ({rows.length} lines)
            </td>
            <td className="py-3 px-2">{formatToZMW(total)}</td>
            <td colSpan={DETAIL_COLUMNS.length - 4} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
